import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  RotateCw,
  Loader2,
  Ban,
  Send,
  SkipForward,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  emailQueueAPI,
  type EmailQueueEntry,
  type EmailQueueStats,
} from '../../lib/api/emailQueue';

const STATUS_CONFIG: Record<string, { color: string; bg: string; icon: React.ComponentType<{ className?: string }> }> = {
  pending: { color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', icon: Clock },
  sending: { color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', icon: Send },
  sent: { color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle },
  failed: { color: 'text-red-700', bg: 'bg-red-50 border-red-200', icon: XCircle },
  cancelled: { color: 'text-gray-600', bg: 'bg-gray-50 border-gray-200', icon: Ban },
  skipped: { color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200', icon: SkipForward },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG['pending'];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      <Icon className="h-3 w-3" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '-';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// Stat Cards
// ---------------------------------------------------------------------------

function StatCards({ stats }: { stats: EmailQueueStats }) {
  const cards = [
    { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Sending', value: stats.sending, icon: Send, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Sent (24h)', value: stats.sent_24h, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Failed', value: stats.failed, icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Cancelled', value: stats.cancelled, icon: Ban, color: 'text-gray-500', bg: 'bg-gray-50' },
    { label: 'Skipped', value: stats.skipped, icon: SkipForward, color: 'text-purple-600', bg: 'bg-purple-50' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
      {cards.map(s => (
        <Card key={s.label} className="relative overflow-hidden">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{s.label}</p>
                <p className="text-2xl font-bold mt-1">{s.value.toLocaleString()}</p>
              </div>
              <div className={`rounded-lg p-2.5 ${s.bg}`}>
                <s.icon className={`h-5 w-5 ${s.color}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function EmailQueueDashboard() {
  const { user } = useAuth();
  // Same gate as Platform Admin (Helix tenancy) — the queue is platform
  // infrastructure and every endpoint behind it 403s other tenants. Without
  // this a customer admin who typed the URL saw a page of raw
  // "Request failed with status code 403" toasts.
  const isHelixUser = user?.platform === 'HELIX' || user?.company_type === 'helix';
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState<EmailQueueStats | null>(null);
  const [entries, setEntries] = useState<EmailQueueEntry[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Action loading states
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 400);
  }, []);

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const params: Record<string, string | number | boolean> = {
        page,
        page_size: pageSize,
        ordering: '-created_at',
      };
      if (statusFilter !== 'all') params['status'] = statusFilter;
      if (typeFilter !== 'all') params['notification_type'] = typeFilter;
      if (search) params['search'] = search;

      const [statsRes, entriesRes] = await Promise.all([
        emailQueueAPI.getStats(),
        emailQueueAPI.getEntries(params),
      ]);

      setStats(statsRes.data);
      setEntries(entriesRes.data.results);
      setTotalCount(entriesRes.data.count);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load email queue data';
      toast.error(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, statusFilter, typeFilter, search]);

  useEffect(() => {
    if (!isHelixUser) return;
    fetchData();
  }, [fetchData, isHelixUser]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!isHelixUser) return;
    const interval = setInterval(() => fetchData(true), 10000);
    return () => clearInterval(interval);
  }, [fetchData, isHelixUser]);

  const handleCancel = async (id: number) => {
    setCancellingId(id);
    try {
      await emailQueueAPI.cancelEntry(id);
      toast.success('Email cancelled');
      fetchData(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to cancel email';
      toast.error(msg);
    } finally {
      setCancellingId(null);
    }
  };

  const handleRetry = async (id: number) => {
    setRetryingId(id);
    try {
      await emailQueueAPI.retryEntry(id);
      toast.success('Email queued for retry');
      fetchData(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to retry email';
      toast.error(msg);
    } finally {
      setRetryingId(null);
    }
  };

  const handleCancelAll = async () => {
    setBulkActionLoading(true);
    try {
      await emailQueueAPI.cancelAll();
      toast.success('All pending emails cancelled');
      fetchData(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to cancel all emails';
      toast.error(msg);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleRetryAll = async () => {
    setBulkActionLoading(true);
    try {
      await emailQueueAPI.retryAll();
      toast.success('All failed emails queued for retry');
      fetchData(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to retry all emails';
      toast.error(msg);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  if (!isHelixUser) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You do not have access to the Email Queue.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Mail className="h-6 w-6" />
            Email Queue
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor and manage outbound email delivery
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchData(true)} disabled={refreshing}>
          <RotateCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      {stats && <StatCards stats={stats} />}

      {/* Filters and Bulk Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <Input
          placeholder="Search recipient..."
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="w-full sm:w-64"
        />
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="sending">Sending</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
            <SelectItem value="skipped">Skipped</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="po_created">PO Created</SelectItem>
            <SelectItem value="po_updated">PO Updated</SelectItem>
            <SelectItem value="ncr_raised">NCR Raised</SelectItem>
            <SelectItem value="ncr_updated">NCR Updated</SelectItem>
            <SelectItem value="grn_created">GRN Created</SelectItem>
            <SelectItem value="document_expiry">Document Expiry</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex-1" />

        {/* Bulk Actions */}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" disabled={bulkActionLoading || (stats?.pending === 0)}>
              <Ban className="h-4 w-4 mr-1" />
              Cancel All Pending
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel all pending emails?</AlertDialogTitle>
              <AlertDialogDescription>
                This will cancel {stats?.pending ?? 0} pending email(s). This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Go Back</AlertDialogCancel>
              <AlertDialogAction onClick={handleCancelAll} className="bg-red-600 hover:bg-red-700">
                Cancel All
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Button
          variant="outline"
          size="sm"
          className="text-amber-600 border-amber-200 hover:bg-amber-50"
          disabled={bulkActionLoading || (stats?.failed === 0)}
          onClick={handleRetryAll}
        >
          <RotateCw className="h-4 w-4 mr-1" />
          Retry All Failed
        </Button>
      </div>

      {/* Queue Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Recipient</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Retries</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Sent / Failed</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                    <Mail className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    No emails found
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <QueueRow
                    key={entry.id}
                    entry={entry}
                    cancellingId={cancellingId}
                    retryingId={retryingId}
                    onCancel={handleCancel}
                    onRetry={handleRetry}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalCount > pageSize && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({totalCount} total)
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Queue Row
// ---------------------------------------------------------------------------

interface QueueRowProps {
  entry: EmailQueueEntry;
  cancellingId: number | null;
  retryingId: number | null;
  onCancel: (id: number) => void;
  onRetry: (id: number) => void;
}

function QueueRow({ entry, cancellingId, retryingId, onCancel, onRetry }: QueueRowProps) {
  const truncatedSubject = entry.subject.length > 50
    ? entry.subject.substring(0, 50) + '...'
    : entry.subject;

  const sentOrFailedTime = entry.sent_at
    ? timeAgo(entry.sent_at)
    : entry.cancelled_at
      ? timeAgo(entry.cancelled_at)
      : entry.error_message
        ? <span className="text-xs text-red-600 truncate max-w-[120px] inline-block align-bottom" title={entry.error_message}>{entry.error_message.substring(0, 30)}...</span>
        : '-';

  return (
    <TableRow>
      <TableCell className="font-medium text-sm max-w-[180px] truncate" title={entry.recipient_email}>
        {entry.recipient_email}
      </TableCell>
      <TableCell className="text-sm max-w-[200px] truncate" title={entry.subject}>
        {truncatedSubject}
      </TableCell>
      <TableCell>
        <StatusBadge status={entry.status} />
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {entry.notification_type.replace(/_/g, ' ')}
      </TableCell>
      <TableCell>
        <Badge variant={entry.priority === 'high' ? 'destructive' : entry.priority === 'low' ? 'secondary' : 'outline'} className="text-xs">
          {entry.priority}
        </Badge>
      </TableCell>
      <TableCell className="text-sm tabular-nums">
        {entry.retry_count}/{entry.max_retries}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
        {timeAgo(entry.created_at)}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {sentOrFailedTime}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          {entry.status === 'pending' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
              disabled={cancellingId === entry.id}
              onClick={() => onCancel(entry.id)}
            >
              {cancellingId === entry.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Ban className="h-3 w-3" />}
            </Button>
          )}
          {entry.status === 'failed' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50"
              disabled={retryingId === entry.id}
              onClick={() => onRetry(entry.id)}
            >
              {retryingId === entry.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
